import { type QueryClient, useQuery } from '@tanstack/react-query'
import { ApiError, describeError, unwrap } from '../../lib/api/client'
import type { MeOut } from '../../lib/api/generated'
import { deskConnectApiV1AuthDeskConnectPost, logoutApiV1AuthLogoutPost, zohoStartApiV1AuthZohoStartPost } from '../../lib/api/generated'
import { forgetEverything, setPersistUser } from '../../lib/api/persist'
import { sessionQuery } from '../../lib/api/queries'
import { safeReturnTo, zohoAuthorizeUrl } from '../../lib/safeUrl'
import { isHelpPath } from '../help/security'
import { clearOfflineCaches } from '../mobile/pwa'

/**
 * The browser's view of the session (scope §11). Three answers, never two: signed in, signed out, or
 * unknown because the API cannot be reached. Unknown is NOT signed out (P11): the shell stays up,
 * read-only, on cached data, and nothing redirects.
 */
export type SessionView = { kind: 'signed_in'; me: MeOut } | { kind: 'signed_out' } | { kind: 'unknown'; reason: string }

/** Paths that render without a session. */
export const PUBLIC_PATHS = new Set(['/signin', '/survey'])

/**
 * Sections that never need a staff session: the contractor portal (its own device session), an already-sent job
 * packet at /w (the token is the credential; /work-orders stays staff), the find-a-time links
 * (/availability for a firm, /pick for the customer; the token is the credential) and the public help
 * centre (every nested /help/* page, via isHelpPath). They render without the staff shell (no palette, global keys,
 * SSE or session redirect), so an anonymous contractor can never open staff navigation or fixture data from them
 * (#67 slice 36). Staff Help content is still gated: it comes only from the authenticated API.
 */
export const PUBLIC_PREFIXES = ['/portal', '/availability', '/pick', '/w'] as const

export function isPublicPath(pathname: string, href = pathname): boolean {
  const techVisitLink = pathname.startsWith('/visit-outcome/') && new URLSearchParams(href.split('#', 2)[1] ?? '').has('t')
  return (
    techVisitLink ||
    PUBLIC_PATHS.has(pathname) ||
    isHelpPath(pathname) ||
    PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  )
}

/**
 * Forget everything this browser kept for the signed-in person: the persisted query cache and the service worker's
 * offline API cache (round 7: a shared phone never shows the previous person's cases). Cache Storage failures are
 * swallowed inside clearOfflineCaches; nothing was cached either.
 */
export async function forgetSignedInUser(queryClient: QueryClient, reason: 'sign-out' | 'unauthorized' = 'unauthorized'): Promise<void> {
  forgetEverything(queryClient)
  await clearOfflineCaches(reason)
}

export async function resolveSession(queryClient: QueryClient): Promise<SessionView> {
  try {
    const s = await queryClient.fetchQuery(sessionQuery())
    if (s.signed_in && s.me) {
      // Route loaders run immediately after this guard. Drop another person's hydrated rows before
      // any of them can render (an effect in SignedInShell is one paint too late for role-sensitive data).
      setPersistUser(queryClient, s.me.id)
      return { kind: 'signed_in', me: s.me }
    }
    return { kind: 'signed_out' }
  } catch (err) {
    // A cached answer from before the outage still says who this is.
    const cached = queryClient.getQueryData(sessionQuery().queryKey)
    if (cached?.signed_in && cached.me) {
      setPersistUser(queryClient, cached.me.id)
      return { kind: 'signed_in', me: cached.me }
    }
    return { kind: 'unknown', reason: describeError(err) }
  }
}

export function useSession(): SessionView {
  const q = useQuery(sessionQuery())
  if (q.data?.signed_in && q.data.me) return { kind: 'signed_in', me: q.data.me }
  if (q.data && !q.data.signed_in) return { kind: 'signed_out' }
  return { kind: 'unknown', reason: q.error ? describeError(q.error) : 'checking' }
}

export function useMe(): MeOut | null {
  const s = useSession()
  return s.kind === 'signed_in' ? s.me : null
}

/** Desk writers (manager, cover, tech) need their own Desk token; billing never connects (ADR 0015). */
export function deskConnectNeeded(me: MeOut | null): boolean {
  return Boolean(me?.can_write_desk) && me?.desk_token !== 'connected'
}

/**
 * Calendar connect is a separate grant. Only an explicit missing or failing token prompts.
 * A session payload that does not carry the field is left alone so older fixtures stay quiet.
 */
export function calendarConnectNeeded(me: { can_write_desk?: boolean; calendar_token?: string | null } | null | undefined): boolean {
  if (!me?.can_write_desk) return false
  return me.calendar_token === 'not_connected' || me.calendar_token === 'refresh_failing'
}

export function deskTokenLabel(me: MeOut): string {
  if (!me.can_write_desk) return 'Desk not used (billing)'
  if (me.desk_token === 'connected') return 'Desk connected'
  if (me.desk_token === 'refresh_failing') return 'Desk connection failing: reconnect'
  return 'Desk not connected'
}

/** Navigates the browser to Zoho. Indirection so tests can assert the target without leaving the page. */
export const browser = {
  assign(url: string): void {
    window.location.assign(url)
  },
}

/**
 * Asks the API for the Zoho authorize URL (JSON, so the URL can be checked against the Zoho host
 * allow-list before the browser goes there) and navigates to it.
 */
async function goToZoho(call: Promise<{ data?: unknown; error?: unknown; response?: Response }>): Promise<void> {
  const out = await unwrap(call as Promise<{ data?: { authorize_url?: unknown }; error?: unknown; response?: Response }>)
  const url = zohoAuthorizeUrl(out?.authorize_url)
  if (!url) throw new ApiError(502, 'bad_authorize_url', 'Sign-in answered with an unexpected address; not following it')
  browser.assign(url)
}

export function startSignIn(returnTo: string | undefined): Promise<void> {
  return goToZoho(
    zohoStartApiV1AuthZohoStartPost({ query: { return_to: safeReturnTo(returnTo) }, headers: { Accept: 'application/json' } }),
  )
}

export function startConnectDesk(returnTo: string | undefined): Promise<void> {
  return goToZoho(
    deskConnectApiV1AuthDeskConnectPost({ query: { return_to: safeReturnTo(returnTo) }, headers: { Accept: 'application/json' } }),
  )
}

/** Revokes the session server-side, then forgets everything this browser cached. */
export async function signOut(queryClient: QueryClient): Promise<void> {
  try {
    await unwrap(logoutApiV1AuthLogoutPost())
  } catch (err) {
    // Already signed out (401) is fine. Every other failure may have left the server-side session live,
    // so keep the local state and tell the user that sign-out did not complete.
    if (!(err instanceof ApiError && err.status === 401)) throw err
  }
  await forgetSignedInUser(queryClient, 'sign-out')
}

/** Error codes the OAuth callbacks put in `?error=` (apps/api/app/api/v1/auth.py), in words. */
export const SIGN_IN_ERRORS: Record<string, string> = {
  not_invited: 'This Zoho account is not on the Patchbay invite list. Ask a manager to invite you.',
  inactive: 'Your Patchbay account is switched off. Ask a manager.',
  zoho_denied: 'Zoho sign-in was cancelled.',
  state_mismatch: 'The sign-in link expired or was opened in another browser. Try again.',
  state_invalid: 'The sign-in link expired or was already used. Try again.',
  state_user_mismatch: 'That Desk grant was started by a different person. Try again from your own session.',
  desk_identity_mismatch: 'That Desk account is not yours. Connect Desk with your own Zoho login.',
  calendar_identity_mismatch: 'That Zoho Calendar is not yours. Connect Calendar with your own Zoho login.',
  id_token_missing: 'Zoho did not confirm which account granted Calendar. Connect Calendar again.',
  desk_agent_mismatch: 'That Desk agent differs from the one mapped to you. Ask a manager to check People.',
  bad_accounts_server: 'Zoho answered from an unexpected data centre. Try again.',
  not_configured: 'Zoho sign-in is not configured on this server yet.',
  role: 'Your role does not connect Desk.',
}

export function signInErrorText(code: string | undefined): string | null {
  if (!code) return null
  return SIGN_IN_ERRORS[code] ?? `Sign-in failed (${code}). Try again.`
}
